import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminUserDrilldown from "@/pages/AdminUserDrilldown";
import Unsubscribe from "@/pages/Unsubscribe";
import CreatorDashboard from "@/pages/CreatorDashboard";
import JoinCreator from "@/pages/JoinCreator";
import PostDetail from "@/pages/PostDetail";
import CreatePost from "@/pages/CreatePost";
import Profile from "@/pages/Profile";
import AuthCallback from "@/pages/AuthCallback";
import Admin from "@/pages/Admin";
import Rules from "@/pages/Rules";
import Messages from "@/pages/Messages";
import Marketplace from "@/pages/Marketplace";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import SellerApply from "@/pages/SellerApply";
import SellerDashboard from "@/pages/SellerDashboard";
import CrumbAnalyzer from "@/pages/CrumbAnalyzer";
import MyBakes from "@/pages/MyBakes";
import RecipeScaler from "@/pages/RecipeScaler";
import MyRecipes from "@/pages/MyRecipes";
import FormulaCalculator from "@/pages/FormulaCalculator";
import DietaryAdaptor from "@/pages/DietaryAdaptor";
import IngredientSubstitute from "@/pages/IngredientSubstitute";
import CostingCalculator from "@/pages/CostingCalculator";
import InvoiceGenerator from "@/pages/InvoiceGenerator";
import Inventory from "@/pages/Inventory";
import LabelDesigner from "@/pages/LabelDesigner";
import LedgerTracker from "@/pages/LedgerTracker";
import Classifieds from "@/pages/Classifieds";
import FindABaker from "@/pages/FindABaker";
import FinderSettings from "@/pages/FinderSettings";
import Upgrade from "@/pages/Upgrade";
import PremiumGate from "@/components/PremiumGate";
import LogoMaker from "@/pages/LogoMaker";
import SocialPostGenerator from "@/pages/SocialPostGenerator";
import NewsletterTemplates from "@/pages/NewsletterTemplates";
import PhotoEnhancer from "@/pages/PhotoEnhancer";
import CartBrandingPreview from "@/pages/CartBrandingPreview";
import RulesAcceptanceModal from "@/components/RulesAcceptanceModal";
import Footer from "@/components/Footer";

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  // Subdomain shortcut: when visitors land on finder.cakepoptalk.com (or any host starting with
  // "finder."), send them straight to the Find-a-Local-Stand map instead of the community home.
  // Only fires at the root path so deep links to /p/:id, /shop/:username, etc. still work as-is.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname || "";
    if (host.startsWith("finder.") && location.pathname === "/") {
      navigate("/find-a-baker", { replace: true });
    }
  }, [location.pathname, navigate]);
  // Fire a Google Analytics pageview whenever the SPA route changes so gtag catches every screen,
  // not just the initial load.
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
  // Handle Emergent OAuth callback fragment before any protected routing
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const isShopStandalone = location.pathname.startsWith("/shop/");
  return (
    <>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/c/:slug" element={<Home />} />
      <Route path="/p/:id" element={<PostDetail />} />
      <Route path="/new" element={<CreatePost />} />
      <Route path="/u/:username" element={<Profile />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/join/:slug" element={<JoinCreator />} />
      <Route path="/creator-dashboard/:token" element={<CreatorDashboard />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/users/:userId" element={<AdminUserDrilldown />} />
      <Route path="/rules" element={<Rules />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/messages/:userId" element={<Messages />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/store-front" element={<Marketplace />} />
      <Route path="/shop/:username" element={<Shop />} />
      <Route path="/shop/:username/p/:slug" element={<Product />} />
      <Route path="/seller/apply" element={<PremiumGate><SellerApply /></PremiumGate>} />
      <Route path="/seller/dashboard" element={<PremiumGate><SellerDashboard /></PremiumGate>} />
      <Route path="/analyzer" element={<PremiumGate><CrumbAnalyzer /></PremiumGate>} />
      <Route path="/my-bakes" element={<PremiumGate><MyBakes /></PremiumGate>} />
      <Route path="/scaler" element={<PremiumGate><RecipeScaler /></PremiumGate>} />
      <Route path="/my-recipes" element={<PremiumGate><MyRecipes /></PremiumGate>} />
      <Route path="/calculator" element={<PremiumGate><FormulaCalculator /></PremiumGate>} />
      <Route path="/adaptor" element={<PremiumGate><DietaryAdaptor /></PremiumGate>} />
      <Route path="/substitute" element={<PremiumGate><IngredientSubstitute /></PremiumGate>} />
      <Route path="/costing" element={<PremiumGate><CostingCalculator /></PremiumGate>} />
      <Route path="/invoice" element={<PremiumGate><InvoiceGenerator /></PremiumGate>} />
      <Route path="/inventory" element={<PremiumGate><Inventory /></PremiumGate>} />
      <Route path="/label" element={<PremiumGate><LabelDesigner /></PremiumGate>} />
      <Route path="/ledger" element={<PremiumGate><LedgerTracker /></PremiumGate>} />
      <Route path="/classifieds" element={<Classifieds />} />
      <Route path="/find-a-baker" element={<FindABaker />} />
      <Route path="/find-a-baker/settings" element={<FinderSettings />} />
      <Route path="/upgrade" element={<Upgrade />} />
      <Route path="/logo-maker" element={<PremiumGate><LogoMaker /></PremiumGate>} />
      <Route path="/social-post" element={<PremiumGate><SocialPostGenerator /></PremiumGate>} />
      <Route path="/newsletter" element={<PremiumGate><NewsletterTemplates /></PremiumGate>} />
      <Route path="/photo-enhancer" element={<PremiumGate><PhotoEnhancer /></PremiumGate>} />
      <Route path="/cart-branding" element={<PremiumGate><CartBrandingPreview /></PremiumGate>} />
    </Routes>
    {!isShopStandalone && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <RulesAcceptanceModal />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
